import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { useAuth } from "../auth";
import { apiGet, apiSend } from "../api";
import DocViewer, { docTypeIcon } from "../components/DocViewer";
import { docExpiryStatus } from "../dateutil";
import { C, R, S } from "../theme";

const RIDE_CATS = ["Test Certificate", "Insurance", "RAMS", "Manual", "Other"];
const CATERING_CATS = ["Food Hygiene Rating", "Gas Safety (CP44)", "PAT Testing", "HACCP/SFBB Manual", "Insurance", "Other"];

const expiryPill = (level) => level === "expired" ? { bg: C.red, fg: "#fff" } : level === "soon" ? { bg: C.amber, fg: C.black } : { bg: "rgba(16,185,129,0.15)", fg: C.green };

export default function Documents() {
  const { aid } = useParams();
  const { isManager } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("Other");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [category, setCategory] = useState(null);
  const CATS = category === "catering" ? CATERING_CATS : RIDE_CATS;

  const load = () => apiGet(`/attractions/${aid}/documents`).then(setDocs).catch(() => {});
  useEffect(() => {
    load();
    apiGet(`/attractions/${aid}`).then((a) => setCategory(a.category)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(f); });
      await apiSend(`/attractions/${aid}/documents`, "POST", { name: f.name, category: cat, mime: f.type || "application/octet-stream", content: b64, size: f.size, expiry_date: expiry || null });
      setExpiry("");
      load();
    } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Document Vault</div>
      </div>
      <div style={{ padding: S.lg }}>
        {isManager && (
          <>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Category</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: S.sm, marginBottom: S.md }}>
              {CATS.map((c) => <button key={c} onClick={() => setCat(c)} style={{ background: cat === c ? C.amber : C.card2, borderRadius: R.pill, padding: "8px 14px", color: cat === c ? C.black : C.sub, fontWeight: 700, fontSize: 13, border: `1px solid ${C.borderStrong}` }}>{c}</button>)}
            </div>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase" }}>Expiry / renewal date (optional)</div>
            <input data-testid="doc-expiry-input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
              style={{ background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, padding: `0 ${S.md}px`, minHeight: 52, width: "100%", outline: "none", boxSizing: "border-box" }} />
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6, marginBottom: S.md }}>Set the renewal date for Insurance, ADIPS, Gas Safe, Risk Assessments or Food Hygiene certs — we'll flag it before it lapses.</div>
            <input ref={fileRef} type="file" onChange={upload} style={{ display: "none" }} />
            <button data-testid="doc-upload" className="pressable" onClick={() => fileRef.current.click()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, background: C.amber, borderRadius: R.md, minHeight: 56, width: "100%", color: C.black, fontWeight: 900, marginBottom: S.lg }}><Icon name="add" size={22} color={C.black} /> {busy ? "Uploading…" : "Upload Document"}</button>
          </>
        )}
        {docs.length === 0 ? <div style={{ color: C.muted, textAlign: "center", padding: S.xl }}>No documents uploaded</div> : docs.map((d) => {
          const st = docExpiryStatus(d.expiry_date);
          const pill = expiryPill(st.level);
          return (
            <button key={d.id} data-testid={`doc-card-${d.id}`} className="pressable" onClick={() => setActive(d)} style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", textAlign: "left", background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${st.level === "expired" ? C.red : st.level === "soon" ? C.amber : C.border}`, cursor: "pointer" }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: C.card2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={docTypeIcon(d.mime, d.name)} size={22} color={C.blue} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.text, fontWeight: 700, wordBreak: "break-word" }}>{d.name}</div>
                <div style={{ color: C.muted, fontSize: 12 }}>{d.category}</div>
                {st.level !== "none" && (
                  <span data-testid={`doc-expiry-badge-${d.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, background: pill.bg, color: pill.fg, fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: R.sm }}>
                    <Icon name="calendar" size={11} color={pill.fg} /> {st.label}
                  </span>
                )}
              </div>
              <Icon name="chevron-forward" size={18} color={C.muted} />
            </button>
          );
        })}
      </div>
      <DocViewer doc={active} canDelete={isManager} onClose={() => setActive(null)} onDeleted={() => { setActive(null); load(); }} onUpdated={() => { setActive(null); load(); }} />
    </div>
  );
}
